'use client'

// You, in his town.
//
// One ladder: how he sees you, which is what he kept of what you left him.
// His memory of you, and the only thing that is his opinion.
//
// Same slide-up as the shrine (shared shell in Drawer.jsx): cream cards,
// ink outline, solid offset shadow, nothing tinted, nothing small.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAccount, authFetch } from '../auth/useUser'
import { AuthSlot, SkeletonCard } from '../auth/AuthSlot'
import useGameStore from '../../stores/gameStore'
import { Drawer, Card, Pill, Meter } from './Drawer'
import { KawaiiCta } from './KawaiiButton'
import { kawaii, ui } from '../../lib/ui/theme'
import { BIRD_NAME } from '../../lib/world/identity'
import { Tabs } from './Tabs'
import { PricePanel } from './PricePanel'
import { MentionsPane } from './Mentions'
import { StandingDrawer } from './Standing'
import { profileNotices } from '../../lib/ui/notices'
import { TIERS } from '../../lib/tiers'

/**
 * How far along the CURRENT leg you are, not how far through your whole
 * history: at the top of a ladder the bar is full, and one past a doorstep
 * still shows a sliver so the bar never reads as empty when it is not.
 */
function legProgress(value, min, nextMin) {
  if (nextMin == null) return 1
  const span = Math.max(1, nextMin - min)
  return Math.max(0.05, Math.min(1, (value - min) / span))
}

export function ProfileDrawer() {
  const open = useGameStore((s) => s.profileOpen)
  const setOpen = useGameStore((s) => s.setProfileOpen)
  const { signedIn } = useAccount()
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)
  // The ladder, one drawer up. Local rather than in the store: it is
  // opened from exactly one place and nothing else needs to know.
  const [standingOpen, setStandingOpen] = useState(false)

  const pull = useCallback(async () => {
    try {
      const res = await authFetch('/api/profile', { cache: 'no-store' })
      const body = await res.json().catch(() => null)
      if (body) { setData(body); setFailed(false) } else setFailed(true)
    } catch { setFailed(true) }
  }, [])

  // Refresh on open, then slowly: a verdict can land while the drawer sits
  // there. signedIn is a dep on purpose — the moment the
  // connect card finishes, the signed-out payload in `data` is stale and
  // must be replaced before the signed-in view reads tier off it.
  useEffect(() => {
    if (!open) return
    pull()
    const id = setInterval(pull, 45_000)
    return () => clearInterval(id)
  }, [open, signedIn, pull])

  // Your rank, remembered between visits, so a promotion can announce
  // itself. Compared on every pull rather than only on open: the drawer
  // polls while it sits there and a rank can move under you.
  const seenRank = useGameStore((s) => s.seenRank)
  const setSeenRank = useGameStore((s) => s.setSeenRank)
  const setNotice = useGameStore((s) => s.setNotice)
  const clearNotice = useGameStore((s) => s.clearNotice)
  const notices = useGameStore((s) => s.notices)
  const shrineUnseen = useGameStore((s) => s.shrineUnseen)
  // The tab lives in the store so whoever opens the drawer can choose
  // where it lands (the since-you-were-here strip lands on mentions).
  const tab = useGameStore((s) => s.profileTab)
  const setTab = useGameStore((s) => s.setProfileTab)

  useEffect(() => {
    const name = data?.tier?.name
    if (!name) return
    if (seenRank && seenRank !== name) setNotice('you', true)
    // First sight of any rank is not a change, it is a baseline.
    if (!seenRank) setSeenRank(name)
  }, [data?.tier?.name, seenRank, setSeenRank, setNotice])

  // Looking at a tab is what clears its dot, which is the only definition
  // that does not lie: the drawer being open says nothing about whether you
  // saw the thing.
  useEffect(() => {
    if (!open) return
    if (tab === 'you') {
      clearNotice('you')
      if (data?.tier?.name) setSeenRank(data.tier.name)
    }
    // Mentions absorbed the offerings tab, so it puts out both dots. The
    // clock itself is moved by the pane once it has read, not from here:
    // marking seen in parallel with the pane's own fetch is a race the
    // mark wins (the read waits on the sim first), and the page would
    // never be able to say which of them were new.
    if (tab === 'mentions') {
      clearNotice('mentions')
      clearNotice('offerings')
    }
  }, [open, tab, data?.tier?.name, clearNotice, setSeenRank])

  // The mentions dot, fed in the background. Cheap on the server (a peek
  // is one count) and the poll is slow: his life moves at diary speed, and
  // three minutes late is still miles ahead of "never knew".
  useEffect(() => {
    if (!signedIn) return
    let dead = false
    const look = () => authFetch('/api/mentions?peek=1', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { if (!dead && typeof d.unseen === 'number') setNotice('mentions', d.unseen > 0) })
      .catch(() => {})
    look()
    const t = setInterval(look, 180_000)
    return () => { dead = true; clearInterval(t) }
  }, [signedIn, setNotice])

  if (!open) return null

  // Computed by the same helper the corner button uses. They used to be two
  // sums: the button folded unseen verdicts into its dot and the tabs read
  // the raw flags, so a lit corner could open onto a row of tabs with
  // nothing marked, which is exactly what Sam hit.
  const badges = profileNotices({ notices, shrineUnseen })

  const TABS = [
    { id: 'you', label: 'You', notice: badges.you ? 'you' : null },
    { id: 'mentions', label: 'Mentions', notice: badges.mentions ? 'mentions' : null },
    { id: 'settings', label: 'Settings' },
  ]

  // The body scrolls on desktop too. It used to be md:overflow-hidden, on
  // the theory that every tab was laid out to fit exactly, and the moment
  // one of them did not (the standing portrait is tall) the row underneath
  // was simply unreachable: no scrollbar, no clue, just missing. Tabs that
  // manage their own inner scroll are unaffected. They size to md:h-full
  // and so never hand this one anything to scroll.
  const BODY = 'kawaii-scroll flex-1 overflow-y-auto min-h-0 px-6 sm:px-10 pb-14 pt-1'

  return (
    <Drawer
      header={<Tabs tabs={TABS} active={tab} onPick={setTab} />}
      onClose={() => setOpen(false)}
      height="min(80dvh, 800px)"
      bodyClass={BODY}
    >
      {!signedIn && tab !== 'settings' && (
        <div className="max-w-5xl mx-auto grid gap-5 md:grid-cols-2 md:gap-8 pt-1 items-stretch">
          {/* The island portals the real connect card in here; bars until
              privy has streamed in (it loads off the boot path now). */}
          <AuthSlot kind="connect" className="h-full" skeleton={<SkeletonCard tall bars={[70, 92, 55, 40]} />} />
          {/* Why bother signing up. The pull is the free mechanic: leave
              him things, he judges them, influence follows. Anyone can be
              part of it without spending anything, which is the whole
              pitch and so it goes first. */}
          <Card className="h-full">
            <p style={{ color: kawaii.ink, fontSize: 16, fontWeight: 800, lineHeight: 1.4, marginBottom: 10 }}>
              Why connect?
            </p>
            <p style={{ color: kawaii.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.6, marginBottom: 10 }}>
              So you can leave {BIRD_NAME} offerings: a message or a picture,
              dropped into his world for him to find. He reads every one and
              decides for himself whether he likes it.
            </p>
            <p style={{ color: kawaii.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.6, marginBottom: 10 }}>
              The more offerings he accepts, the more he remembers you, and
              the more influence you gain in his world. It costs nothing,
              and he treats everyone by exactly the same rule: he either
              likes what you left him, or he doesn't.
            </p>
            <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, lineHeight: 1.6 }}>
              Signing up takes an email and nothing else.
            </p>
          </Card>
        </div>
      )}

      {tab === 'settings' && <SettingsPane data={data} onSaved={pull} />}

      {signedIn && tab !== 'settings' && (<>
        {!data && !failed && tab !== 'settings' && (
          // The skeleton IS the layout: same grids, same column spans, so
          // the real content replaces grey bars without a single element
          // moving. A line of italic text here was a guaranteed jump.
          // Settings is static and loads nothing, so it skips the wait.
          tab === 'mentions' ? (
            <div className="max-w-5xl mx-auto grid gap-5 md:grid-cols-2 md:gap-8 pt-1">
              <div className="min-w-0"><SkeletonCard tall bars={[70, 90, 84, 62, 40]} /></div>
              <div className="min-w-0 space-y-3">
                <SkeletonCard bars={[55, 85, 30]} />
                <SkeletonCard bars={[62, 78, 26]} />
                <SkeletonCard bars={[48, 88, 34]} />
              </div>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto grid gap-5 md:grid-cols-3 md:gap-8">
              <div className="md:col-span-1"><SkeletonCard tall bars={[60, 40, 85, 70, 55, 30]} /></div>
              <div className="md:col-span-2 flex flex-col gap-5">
                <SkeletonCard bars={[45, 30, 80]} />
                <SkeletonCard bars={[70, 88, 52, 64]} />
              </div>
            </div>
          )
        )}
        {failed && (
          <div className="mx-auto w-full max-w-lg pt-2">
            <Card>
              <p style={{ color: kawaii.ink, fontSize: 14.5, fontWeight: 700, lineHeight: 1.5 }}>
                could not read your standing just now.
              </p>
              <div style={{ marginTop: 12 }}>
                <KawaiiCta intent="primary" small onClick={pull}>try again</KawaiiCta>
              </div>
            </Card>
          </div>
        )}
        {/* data.tier (not just data) — a payload fetched while signed out
            has no tier, and rendering off it crashed the whole page the
            moment someone finished the connect flow. */}
        {data?.tier && tab === 'you' && (
          // Who you are to him, then the maker's mood over the town.
          //
          // Your offerings used to sit in the other half of that bottom row
          // and they are gone from here: the offerings tab is two clicks
          // away and shows all of them rather than one at a time, so this
          // was the same list in a worse form, and it was the thing making
          // the page too tall to see the end of.
          <div className="max-w-5xl mx-auto grid gap-5 md:grid-cols-3 md:gap-8">
            {/* Once, until answered: the letters question, to anyone with
                an address on their account. Closing it is an answer too. */}
            <div className="md:col-span-3"><LettersAsk /></div>
            <div className="md:col-span-1"><StandingCard data={data} onOpen={() => setStandingOpen(true)} /></div>
            {/* The right column is one column, not one row: the account
                card and the maker's mood stack inside it and both stop at
                the standing portrait's edge. */}
            <div className="md:col-span-2 flex flex-col gap-5">
              {/* The account card arrives by portal from the auth island. */}
              <AuthSlot kind="account" skeleton={<SkeletonCard bars={[45, 30]} />} />
              {/* The maker's mood: the one place the project's token
                  ($PINO since 31 Oct) is read, as weather over his town.
                  Sam, 3 Oct: everything else to do with a token came out. */}
              <div className="min-w-0">
                <PricePanel />
              </div>
            </div>
          </div>
        )}


        {tab === 'mentions' && <MentionsPane offerings={data?.offerings || null} standing={data?.standing || null} />}
      </>)}

      {standingOpen && data?.tier && (
        <StandingDrawer data={data} onClose={() => setStandingOpen(false)} />
      )}
    </Drawer>
  )
}

// ── His side ────────────────────────────────────────────────────────────

// The whole climb at a glance, beside the card: one vertical bar per
// step, past steps full, the current one filling bottom-up with your
// progress through it, the rest empty. Never labelled with a number or
// the word rank; the art and the names carry the meaning, this carries
// the shape of the journey.
function TierLadder({ kept }) {
  // TIERS is ordered top-down; climb reads bottom-up, so render as-is
  // (first row = furthest step) and let the column stack from the top.
  const steps = [...TIERS].map((t, i, arr) => {
    const nextMin = i === 0 ? null : arr[i - 1].min
    return { ...t, nextMin }
  })
  return (
    <div
      aria-hidden="true"
      style={{ display: 'flex', flexDirection: 'column', gap: 5, width: 16, alignSelf: 'stretch', minHeight: 120 }}
    >
      {steps.map((t) => {
        const span = (t.nextMin ?? t.min * 2) - t.min || 1
        const fill = Math.max(0, Math.min(1, (kept - t.min) / span))
        const full = t.nextMin !== null && kept >= t.nextMin
        return (
          <div
            key={t.name}
            title={t.name}
            style={{
              flex: 1,
              borderRadius: 8,
              border: kawaii.outlineThin,
              background: kawaii.creamDeep,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            <div
              style={{
                height: `${(full ? 1 : fill) * 100}%`,
                background: t.color,
                transition: 'height 500ms ease',
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

function StandingCard({ data, onOpen = null }) {
  const { username } = useAccount()
  const { tier, standing } = data
  const kept = standing.accepted
  const pct = legProgress(kept, tier.min, tier.nextMin)

  return (
    <Card>
      {/* Full bleed to the card's own padding. It was capped at 180px, which
          left a moat of cream on either side and made the best art in the
          product look like a thumbnail that had failed to load. */}
      {tier.card && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'stretch' }}>
          <img
            src={tier.card}
            alt=""
            style={{
              display: 'block', minWidth: 0, flex: 1,
              border: kawaii.outlineThin, borderRadius: 14, boxShadow: kawaii.dropSm,
            }}
          />
          <TierLadder kept={kept} />
        </div>
      )}
      <div style={{ marginBottom: 10 }}><Pill bg={tier.color}>{tier.name}</Pill></div>
      {/* The job here is the art, the name and the distance to the next
          one; the counts stay off the card. */}
      {tier.next && (
        <>
          <div style={{ marginTop: 12, marginBottom: 6 }}>
            <Meter pct={pct} color="#f0b064" />
          </div>
          <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5 }}>
            {tier.toNext === 1
              ? `one more accepted offering and you are ${tier.next.toLowerCase()}.`
              : `${tier.toNext} more accepted offerings and you are ${tier.next.toLowerCase()}.`}
          </p>
        </>
      )}
      {!tier.next && (
        <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, marginTop: 10 }}>
          he could not know you better{username ? `, ${username}` : ''}.
        </p>
      )}
      {/* What the name means, when you want it, rather than a ladder
          sitting open under everyone's portrait forever. It opens over
          this drawer, so closing it puts you back where you were. */}
      {onOpen && (
        <div style={{ marginTop: 12 }}>
          <KawaiiCta small onClick={onOpen}>how he sees you</KawaiiCta>
        </div>
      )}
    </Card>
  )
}

// ── Settings ────────────────────────────────────────────────────────────
//
// The name is LIVE: it saves to users.called and every offering submitted
// after that carries it, so his verdicts, threads and the feed use it
// while his memory keeps filing you under the generated id. The charset
// mirrors the sim's validateHandle (refused here with words rather than
// clamped to 'anon' there in silence). Consent ships disabled: the tab
// shows the road before the road exists, and the toggle will gate every
// path that leaves the site when it wakes.

// The toggle, drawn rather than a checkbox in disguise: trough in the
// pressed cream, knob carrying the same solid offset shadow as the cards.
// Disabled is a real state here, not a greyed accident: the whole row
// still reads, only the hand is told no.
function ConsentToggle({ on = false, disabled = true, onChange = null }) {
  const flip = () => { if (!disabled && onChange) onChange(!on) }
  return (
    <span
      role="switch"
      tabIndex={disabled ? -1 : 0}
      aria-checked={on}
      aria-disabled={disabled}
      onClick={flip}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); flip() } }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        width: 58,
        height: 32,
        flexShrink: 0,
        borderRadius: kawaii.radiusPill,
        border: kawaii.outlineThin,
        background: on ? '#a8d6a0' : kawaii.creamDeep,
        padding: 3,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: kawaii.cream,
          border: kawaii.outlineThin,
          boxShadow: kawaii.dropSm,
          transform: on ? 'translateX(26px)' : 'translateX(0)',
          transition: 'transform 180ms ease',
        }}
      />
    </span>
  )
}

function SettingsPane({ data = null, onSaved = null }) {
  const { signedIn, username } = useAccount()
  const [val, setVal] = useState('')
  const [state, setState] = useState('idle')   // idle | saving | saved | error
  const [err, setErr] = useState('')

  // Prefill from the profile once it lands; a save updates it upstream.
  useEffect(() => { setVal(data?.called || '') }, [data?.called])

  const dirty = (val || '') !== (data?.called || '')

  const save = async () => {
    setState('saving'); setErr('')
    try {
      const res = await authFetch('/api/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ called: val.trim() }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) { setErr(body?.error || 'could not save.'); setState('error'); return }
      setState('saved')
      onSaved?.()
      setTimeout(() => setState('idle'), 2500)
    } catch {
      setErr('could not save.'); setState('error')
    }
  }

  return (
    <div className="max-w-5xl mx-auto grid gap-5 md:grid-cols-2 md:gap-8 pt-1 items-start">
      <Card>
        <p style={{ color: kawaii.ink, fontSize: 16, fontWeight: 800, lineHeight: 1.4, marginBottom: 8 }}>
          How should {BIRD_NAME} refer to you?
        </p>
        <p style={{ color: kawaii.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.6, marginBottom: 12 }}>
          {username
            ? <>He knows you as <strong>{username}</strong>, and that stays your id in his world. Give him a name here and he will use it instead: in his diary, his replies, anywhere he mentions you, from now on. What he already made keeps the name it was made with.</>
            : <>He gives everyone a generated name when they connect; that stays your id in his world. Connect, and you can give him a name to actually call you.</>}
        </p>
        <div className="flex items-center gap-3">
          <input
            type="text"
            disabled={!signedIn || state === 'saving'}
            value={val}
            onChange={(e) => { setVal(e.target.value); if (state !== 'idle') setState('idle'); setErr('') }}
            onKeyDown={(e) => { if (e.key === 'Enter' && dirty && signedIn) save() }}
            placeholder={signedIn ? (username || 'the name he should use') : 'connect first'}
            aria-label={`the name ${BIRD_NAME} should use for you`}
            maxLength={24}
            style={{
              flex: 1,
              minWidth: 0,
              padding: '11px 14px',
              borderRadius: 14,
              border: kawaii.outlineThin,
              background: signedIn ? kawaii.cream : kawaii.creamDeep,
              color: kawaii.ink,
              fontFamily: ui.font,
              fontSize: 14,
              fontWeight: 600,
              opacity: signedIn ? 1 : 0.55,
              cursor: signedIn ? 'text' : 'not-allowed',
            }}
          />
          <KawaiiCta small disabled={!signedIn || !dirty || state === 'saving'} onClick={save}>
            {state === 'saving' ? 'saving' : state === 'saved' ? 'saved' : 'save'}
          </KawaiiCta>
        </div>
        <p style={{ color: err ? '#b3423f' : kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.6, marginTop: 8 }}>
          {err || 'letters, digits, _ - . only, up to 24 characters. leave it empty to go back to your generated name.'}
        </p>
      </Card>

      <LettersCard />
    </div>
  )
}

// ── The letters ─────────────────────────────────────────────────────────
//
// Dev notes and what he has been up to, by email, about once a week. On
// unless you say no (Sam, 31 Oct: it had been off by default), one switch
// here and a one-click unsubscribe in every letter. The state lives on the account (lib/db.js mail_* columns)
// so it follows you between devices; the switch reads it fresh each time
// the tab opens. A wallet-only account has no address to send to, and
// the card says so instead of offering a switch that could not work.

function useLetters() {
  const { signedIn } = useAccount()
  const [state, setState] = useState(null)   // { on, asked, hasEmail } | null
  const [busy, setBusy] = useState(false)
  const pull = useCallback(async () => {
    if (!signedIn) { setState(null); return }
    try {
      const res = await authFetch('/api/letters/consent')
      if (res.ok) setState(await res.json())
    } catch { /* the switch stays quiet */ }
  }, [signedIn])
  useEffect(() => { pull() }, [pull])
  const answer = useCallback(async (body) => {
    setBusy(true)
    try {
      const res = await authFetch('/api/letters/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) setState(await res.json())
    } catch { /* leave it as it was */ } finally { setBusy(false) }
  }, [])
  return { state, busy, answer, signedIn }
}

function LettersCard() {
  const { state, busy, answer, signedIn } = useLetters()
  const on = !!state?.on
  const canSwitch = signedIn && !!state?.hasEmail && !busy
  return (
    <Card>
      <p style={{ color: kawaii.ink, fontSize: 16, fontWeight: 800, lineHeight: 1.4, marginBottom: 8 }}>
        Letters from the town
      </p>
      <div className="flex items-center justify-between gap-4">
        <p style={{ color: kawaii.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.6 }}>
          Send me the letters by email.
        </p>
        <ConsentToggle on={on} disabled={!canSwitch} onChange={(v) => answer({ on: v })} />
      </div>
      <p style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 600, lineHeight: 1.6, marginTop: 10 }}>
        {!signedIn
          ? 'Connect first. The letters go to the email on your account.'
          : state && !state.hasEmail
            ? 'Your account has no email on it (you connected with a wallet), so there is nowhere to send them. Sign in with an email to get them.'
            : `Notes from the person keeping the town about what is being built, and what ${BIRD_NAME} has been up to, about once a week, with pictures. On unless you switch it off, and every letter has a one-click unsubscribe at the bottom.`}
      </p>
    </Card>
  )
}

// The first-visit question, once. Shown on the You tab to anyone with an
// address who has not been asked yet, in the app or by email; closing it
// counts as asked, so nobody is nagged and the invite email skips them.
// Since the letters are on by default (31 Oct) there is nothing to ask
// somebody who already gets them: the switch above is where they say no.
export function LettersAsk() {
  const { state, busy, answer, signedIn } = useLetters()
  if (!signedIn || !state || state.asked || !state.hasEmail || state.on) return null
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <p style={{ color: kawaii.ink, fontSize: 16, fontWeight: 800, lineHeight: 1.4 }}>
          Letters from the town?
        </p>
        <button
          type="button"
          aria-label="not now"
          disabled={busy}
          onClick={() => answer({ dismiss: true })}
          style={{ background: 'none', border: 0, color: kawaii.inkSoft, fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: ui.font }}
        >
          not now
        </button>
      </div>
      <p style={{ color: kawaii.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.6, margin: '8px 0 12px' }}>
        Notes from the person keeping the town about what is being built, and what {BIRD_NAME} has been up to, about once a week, with pictures. To the email on your account, and a one-click unsubscribe in every one.
      </p>
      <div className="flex items-center gap-3">
        <KawaiiCta intent="primary" small disabled={busy} onClick={() => answer({ on: true })}>yes, send them</KawaiiCta>
        <KawaiiCta small disabled={busy} onClick={() => answer({ on: false })}>no thanks</KawaiiCta>
      </div>
    </Card>
  )
}

export default ProfileDrawer
